import { useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CircleAlert, CircleCheck, FileText, Loader2 } from "lucide-react";
import { isFogbugzEvtxAttachmentName } from "@spectree/shared";
import {
  composeToolboxEvtxSummary,
  hasAllEvtxSections,
  type FogbugzEngineErrorCode,
  type FogbugzEvtxDigestResponse,
  type FogbugzToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import { Chip } from "@/components/grooming/grooming-chips";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OpenInToolboxCallout } from "@/components/toolbox/open-in-toolbox-callout";
import {
  useCaseEvtxDigest,
  useFogbugzCase,
  usePostReviewedEvtxSummary,
  type Attachment,
  type CaseEvent,
} from "@/hooks/queries/use-fogbugz";
import { useMe } from "@/hooks/queries/use-me";
import { usePageTitle } from "@/hooks/use-page-title";
import { useFogbugzBridge, useToolboxBridge, useToolsetSupport } from "@/lib/toolbox/bridge";
import { useClaudeRunOutcome } from "@/lib/toolbox/claude-signals";
import { FOGBUGZ_TOOL_NAME, TOOLBOX_HOME_PATH } from "@/lib/toolbox/tools";
import { TOOLBOX_PAGE_TITLE } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";
import { FogbugzCodeSection } from "@/pages/toolbox/fogbugz-code";
import { SUPPORT_LABELS } from "@/pages/toolbox/home";

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests)
// ---------------------------------------------------------------------------

/** A plain message for an engine refusal, and whether Setup checks is where the person fixes it. */
export interface FogbugzEngineMessage {
  text: string;
  setup: boolean;
}

const ENGINE_MESSAGES: Record<FogbugzEngineErrorCode, FogbugzEngineMessage> = {
  CLAUDE_NOT_FOUND: { text: "Claude Code is not installed on this computer, so no summary was written.", setup: true },
  CLAUDE_NOT_SIGNED_IN: {
    text: "Claude Code is not signed in on this computer. Sign in, then summarize again.",
    setup: true,
  },
  API_KEY_BILLING: {
    text: "Claude Code on this computer would bill an API key instead of your Claude subscription, so SpecTree Toolbox did not run it.",
    setup: true,
  },
  USAGE_LIMIT: { text: "Your Claude usage limit was reached. Summarize again once it resets.", setup: false },
  TIMED_OUT: { text: "Claude took too long to write the summary, so it was stopped. Summarize again.", setup: false },
  PLUGIN_MISSING: {
    text: "The SpecTree plugin for Claude Code is missing on this computer, so Claude cannot write the summary.",
    setup: true,
  },
  NO_SUMMARY_RETURNED: {
    text: "Claude finished without a complete summary (all four sections). Summarize again.",
    setup: false,
  },
  CANCELED: { text: "You canceled the summary.", setup: false },
  BUSY: {
    text: "SpecTree Toolbox is already writing an event log summary. Wait for it to finish, then try again.",
    setup: false,
  },
  REFUSED: {
    text: "SpecTree Toolbox stopped this summary before Claude started, because it could not keep the run read-only and private on this computer.",
    setup: true,
  },
  NETWORK: { text: "Claude Code could not reach Anthropic. Check the network connection, then summarize again.", setup: false },
  UNKNOWN: { text: "Claude Code could not write this summary. Summarize again.", setup: false },
};

/**
 * Plain words for a fogbugz engine refusal. For a usage limit the engine's
 * own message (which names the reset time) is added; for REFUSED and
 * CLAUDE_NOT_FOUND the engine's sentence (what to fix, such as "Claude Code
 * 2.1.305 is too old … update it to 2.1.312 or newer") replaces the generic
 * one. Every refusal
 * ends with "Nothing was posted to the case." Pure; exported for tests.
 */
export function fogbugzEngineMessage(code: FogbugzEngineErrorCode, engineMessage?: string): FogbugzEngineMessage {
  const base = ENGINE_MESSAGES[code];
  const detail = engineMessage?.trim();
  let text = base.text;
  if (code === "USAGE_LIMIT" && detail) text = `${base.text} ${detail}`;
  if ((code === "REFUSED" || code === "CLAUDE_NOT_FOUND") && detail) text = detail;
  return { ...base, text: `${text} Nothing was posted to the case.` };
}

/**
 * True when the app's fogbugz toolset offers "Look into the code". Every app
 * that serves fogbugz contract 1 does; the check only keeps a partial bridge
 * from breaking the summary. Pure; exported for tests.
 */
export function hasCodeBridge(fogbugz: FogbugzToolsetBridge): boolean {
  const bridge = fogbugz as Partial<FogbugzToolsetBridge>;
  return (
    typeof bridge.listCodeRefs === "function" &&
    typeof bridge.investigateCode === "function" &&
    typeof bridge.askCode === "function" &&
    typeof bridge.onCodeProgress === "function"
  );
}

/** Digits only, as typed into the case number box. Pure; exported for tests. */
export function caseNumberDigits(value: string): string {
  return value.replace(/\D+/g, "");
}

/**
 * The `.evtx` attachments of a case, in event order, once each (by `sURL`).
 * Other attachments (screenshots, earlier `.evtx-summary.md` files) are left
 * out. Pure; exported for tests.
 */
export function evtxAttachmentsOf(events: readonly CaseEvent[] | undefined): Attachment[] {
  const seen = new Set<string>();
  const out: Attachment[] = [];
  for (const event of events ?? []) {
    for (const attachment of event.rgAttachments ?? []) {
      if (!isFogbugzEvtxAttachmentName(attachment.sFileName) || seen.has(attachment.sURL)) continue;
      seen.add(attachment.sURL);
      out.push(attachment);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// The flow
// ---------------------------------------------------------------------------

/** Where one Summarize is. */
type Run =
  | { phase: "idle" }
  | { phase: "digest"; attachment: Attachment }
  | { phase: "summarizing"; attachment: Attachment; digest: FogbugzEvtxDigestResponse }
  | { phase: "review"; attachment: Attachment; digest: FogbugzEvtxDigestResponse; sections: string }
  | { phase: "refused"; attachment: Attachment; message: FogbugzEngineMessage }
  | { phase: "api-error"; attachment: Attachment; message: string }
  | {
      phase: "posted";
      attachment: Attachment;
      digest: FogbugzEvtxDigestResponse;
      sections: string;
      summaryFileName: string;
    };

const IDLE: Run = { phase: "idle" };

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function Notice({
  tone,
  testId,
  children,
}: {
  tone: "error" | "ok";
  testId: string;
  children: ReactNode;
}) {
  const Icon = tone === "error" ? CircleAlert : CircleCheck;
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      data-testid={testId}
      className={cn(
        "flex items-start gap-2.5 rounded-[10px] border px-4 py-3 text-[13px] leading-relaxed",
        tone === "error" ? "border-red-500/40 bg-red-500/5" : "border-emerald-500/40 bg-emerald-500/5",
      )}
    >
      <Icon
        className={cn("mt-0.5 h-4 w-4 shrink-0", tone === "error" ? "text-red-400" : "text-emerald-400")}
        aria-hidden="true"
      />
      <div className="flex flex-1 flex-col gap-2">{children}</div>
    </div>
  );
}

function Waiting({ label, testId }: { label: string; testId?: string }) {
  return (
    <span className="flex items-center gap-2.5 text-[13px] text-muted-foreground" role="status" data-testid={testId}>
      <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * The case number → `.evtx` list → Summarize → review → Post flow. Every
 * FogBugz read and write goes through the SpecTree API with the person's own
 * FogBugz key; the summary sections come from the person's own Claude through
 * the bridge. Nothing is posted until the person clicks "Post to case #N".
 */
function FogbugzEvtxFlow({ fogbugz, personName }: { fogbugz: FogbugzToolsetBridge; personName: string }) {
  const [caseInput, setCaseInput] = useState("");
  const [caseId, setCaseId] = useState<number | null>(null);
  const [run, setRun] = useState<Run>(IDLE);
  // "Look into the code" shares the lane with Summarize: one FogBugz Claude run at a time.
  const [codeRunning, setCodeRunning] = useState(false);
  // Each Summarize gets a number; a result for an older number (Discard, another case) is ignored.
  const runSeq = useRef(0);
  const reportClaudeRun = useClaudeRunOutcome();
  const caseQuery = useFogbugzCase(caseId);
  const digest = useCaseEvtxDigest();
  const post = usePostReviewedEvtxSummary();

  const evtxAttachments = useMemo(() => evtxAttachmentsOf(caseQuery.data?.data.case.events), [caseQuery.data]);
  const summaryBusy = run.phase === "digest" || run.phase === "summarizing" || post.isPending;
  const busy = summaryBusy || codeRunning;

  const resetRun = () => {
    runSeq.current += 1;
    setRun(IDLE);
    post.reset();
  };

  const onLoad = (event: FormEvent) => {
    event.preventDefault();
    const digits = caseNumberDigits(caseInput);
    if (!digits || busy) return;
    resetRun();
    setCaseId(Number(digits));
  };

  const summarize = async (attachment: Attachment) => {
    if (caseId === null || busy) return;
    runSeq.current += 1;
    const seq = runSeq.current;
    post.reset();
    setRun({ phase: "digest", attachment });
    let digestResult: FogbugzEvtxDigestResponse;
    try {
      digestResult = (await digest.mutateAsync({ id: caseId, sURL: attachment.sURL })).data;
    } catch (error) {
      if (seq === runSeq.current) {
        setRun({ phase: "api-error", attachment, message: errorText(error, `Could not read ${attachment.sFileName}.`) });
      }
      return;
    }
    if (seq !== runSeq.current) return;
    setRun({ phase: "summarizing", attachment, digest: digestResult });
    let result: Awaited<ReturnType<FogbugzToolsetBridge["summarizeEvtx"]>>;
    try {
      result = await fogbugz.summarizeEvtx({
        caseId,
        fileName: digestResult.fileName,
        digestMarkdown: digestResult.digestMarkdown,
      });
    } catch {
      if (seq === runSeq.current) {
        setRun({
          phase: "refused",
          attachment,
          message: {
            text: "SpecTree Toolbox could not run Claude for this summary. Nothing was posted to the case.",
            setup: true,
          },
        });
      }
      return;
    }
    reportClaudeRun(result);
    if (seq !== runSeq.current) return;
    if (!result.ok) {
      setRun({ phase: "refused", attachment, message: fogbugzEngineMessage(result.errorCode, result.message) });
      return;
    }
    // The sections are untrusted model output: all four headings, in order, or no preview and no Post.
    if (!hasAllEvtxSections(result.sections)) {
      setRun({ phase: "refused", attachment, message: fogbugzEngineMessage("NO_SUMMARY_RETURNED") });
      return;
    }
    setRun({ phase: "review", attachment, digest: digestResult, sections: result.sections });
  };

  const cancel = () => {
    if (run.phase === "summarizing") {
      // summarizeEvtx resolves with CANCELED, which shows "You canceled the summary".
      void fogbugz.cancel();
      return;
    }
    if (run.phase === "digest") resetRun();
  };

  const postReviewed = () => {
    if (run.phase !== "review" || caseId === null || post.isPending) return;
    const { attachment, digest: reviewedDigest, sections } = run;
    post.mutate(
      { id: caseId, sURL: reviewedDigest.sURL, sections, digestMarkdown: reviewedDigest.digestMarkdown },
      {
        onSuccess: (response) => {
          setRun({
            phase: "posted",
            attachment,
            digest: reviewedDigest,
            sections,
            summaryFileName: response.data.summaryFileName,
          });
        },
      },
    );
  };

  const reviewDigest = run.phase === "review" ? run.digest : null;
  const reviewSections = run.phase === "review" ? run.sections : null;
  // The digest can run to ~36k characters; compose once per review, not on every render.
  const preview = useMemo(
    () =>
      reviewDigest && reviewSections !== null
        ? composeToolboxEvtxSummary({
            fileName: reviewDigest.fileName,
            personName,
            sections: reviewSections,
            digestMarkdown: reviewDigest.digestMarkdown,
          })
        : null,
    [reviewDigest, reviewSections, personName],
  );

  return (
    <div className="flex w-full max-w-4xl flex-col gap-5 px-4 pb-6 pt-7">
      <div className="flex flex-col gap-1">
        <h1 className={TOOLBOX_PAGE_TITLE}>{FOGBUGZ_TOOL_NAME}</h1>
        <p className="text-[13px] text-muted-foreground">
          Your Claude summarizes an event log (.evtx) attached to a FogBugz case. You review the summary, then post it
          to the case as yourself.
        </p>
      </div>

      <form className="flex items-end gap-2" onSubmit={onLoad} data-testid="fogbugz-case-form">
        <label className="flex flex-col gap-1 text-[13px] font-medium" htmlFor="fogbugz-case-number">
          Case number
          <Input
            id="fogbugz-case-number"
            inputMode="numeric"
            autoComplete="off"
            className="w-40"
            value={caseInput}
            onChange={(event) => {
              setCaseInput(caseNumberDigits(event.target.value));
            }}
          />
        </label>
        <Button type="submit" disabled={!caseInput || busy}>
          Show event logs
        </Button>
      </form>

      {caseId !== null && caseQuery.isLoading && <Waiting label={`Loading case #${String(caseId)}`} />}

      {caseId !== null && caseQuery.isError && (
        <Notice tone="error" testId="fogbugz-case-error">
          <p>{errorText(caseQuery.error, `Could not load case #${String(caseId)}.`)}</p>
        </Notice>
      )}

      {caseId !== null && caseQuery.data && (
        <section className="flex flex-col gap-2" aria-labelledby="fogbugz-evtx-list">
          <h2 id="fogbugz-evtx-list" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA]">
            Case #{caseId}: {caseQuery.data.data.case.sTitle}
          </h2>
          {evtxAttachments.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="fogbugz-no-evtx">
              This case has no event logs (.evtx) attached.
            </p>
          ) : (
            <ul className="glass-card flex flex-col divide-y divide-[#18181B] rounded-[10px]" data-testid="fogbugz-evtx-list">
              {evtxAttachments.map((attachment) => {
                const current = run.phase !== "idle" && run.attachment.sURL === attachment.sURL;
                return (
                  <li key={attachment.sURL} className="flex items-center gap-3 px-4 py-3" data-testid="fogbugz-evtx-row">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate text-sm" title={attachment.sFileName}>
                      {attachment.sFileName}
                    </span>
                    {current && run.phase === "digest" && <Waiting label="Reading the event log" />}
                    {current && run.phase === "summarizing" && <Waiting label="Claude is writing the summary" />}
                    {current && (run.phase === "digest" || run.phase === "summarizing") ? (
                      <Button type="button" variant="outline" size="sm" onClick={cancel}>
                        Cancel
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        aria-label={`Summarize ${attachment.sFileName}`}
                        onClick={() => {
                          void summarize(attachment);
                        }}
                      >
                        Summarize
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {run.phase === "refused" && (
        <Notice tone="error" testId="fogbugz-engine-error">
          <p>{run.message.text}</p>
          {run.message.setup && (
            <Link to="/toolbox/setup" className="text-link hover:underline">
              Open Setup checks
            </Link>
          )}
        </Notice>
      )}

      {run.phase === "api-error" && (
        <Notice tone="error" testId="fogbugz-api-error">
          <p>{run.message}</p>
        </Notice>
      )}

      {run.phase === "review" && preview !== null && (
        <section className="flex flex-col gap-3" aria-labelledby="fogbugz-review" data-testid="fogbugz-review">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="fogbugz-review" className="font-display text-base font-semibold">
              Review the summary of {run.digest.fileName}
            </h2>
            <Chip tone="cur">Not posted yet</Chip>
          </div>
          <p className="text-[13px] text-muted-foreground">
            This is exactly what will be posted to case #{caseId} as {run.digest.summaryFileName}. Check it against
            the event log before you post.
          </p>
          {run.digest.truncated && (
            <p className="text-xs text-amber-500" role="note">
              Parsing stopped early at a record or time limit; the digest covers{" "}
              {run.digest.digestStats.recordsParsed.toLocaleString()} of{" "}
              {run.digest.digestStats.recordsSeen.toLocaleString()} records seen.
            </p>
          )}
          <div
            className="max-h-[60vh] overflow-y-auto rounded-md border border-white/[0.08] bg-white/[0.02] p-4 text-sm [&_td:last-child]:[overflow-wrap:anywhere]"
            data-testid="fogbugz-preview"
          >
            <MarkdownRenderer content={preview} disableLinks />
          </div>
          {post.isError && (
            <Notice tone="error" testId="fogbugz-post-error">
              <p>{errorText(post.error, "Could not post the summary to the case.")}</p>
            </Notice>
          )}
          <div className="flex items-center gap-2">
            <Button type="button" onClick={postReviewed} disabled={post.isPending}>
              {post.isPending ? "Posting…" : `Post to case #${String(caseId)}`}
            </Button>
            <Button type="button" variant="outline" onClick={resetRun} disabled={post.isPending}>
              Discard
            </Button>
          </div>
        </section>
      )}

      {run.phase === "posted" && (
        <Notice tone="ok" testId="fogbugz-posted">
          <p>Posted to the case as {run.summaryFileName}</p>
        </Notice>
      )}

      {/* App only: nothing from this section reaches the reviewed post. Keyed by case and attachment, so
          Discard, Summarize again and another case close it; it stays open after the summary is posted. */}
      {(run.phase === "review" || run.phase === "posted") && caseId !== null && hasCodeBridge(fogbugz) && (
        <FogbugzCodeSection
          key={`${String(caseId)}:${run.attachment.sURL}`}
          fogbugz={fogbugz}
          caseId={caseId}
          fileName={run.digest.fileName}
          digestMarkdown={run.digest.digestMarkdown}
          sections={run.sections}
          laneBusy={summaryBusy}
          onRunningChange={setCodeRunning}
        />
      )}
    </div>
  );
}

/**
 * `/toolbox/fogbugz`: the FogBugz event log summary in SpecTree Toolbox (epic
 * PI-SPEC-SW-E307, feature PI-SPEC-SW-1598, ER-1663 Resolved Decisions 3 and
 * 11). The person types a case number, sees that case's `.evtx` attachments,
 * and on Summarize SpecTree returns the deterministic digest, the person's
 * own Claude writes the four summary sections (`bridge.fogbugz.summarizeEvtx`),
 * and the page shows the summary composed by `composeToolboxEvtxSummary`
 * exactly as it will be posted. Only "Post to case #N" posts it, through the
 * SpecTree API with the person's FogBugz key. Claude refusals and API
 * refusals are plain messages; nothing is posted.
 *
 * Under the review, "Look into the code" (feature PI-SPEC-SW-1604,
 * `FogbugzCodeSection`) lets the person's Claude read the project's code on
 * demand. It shares the one-run lane with Summarize and stays in the app:
 * nothing from it is part of the reviewed post.
 *
 * Behind RequireFogbugzToolbox (the `fogbugz` capability somewhere and a
 * saved FogBugz key). In a plain browser it shows how to open SpecTree
 * Toolbox; in an app that cannot run the fogbugz toolset, the same update
 * words as the home tile. The website's `/fogbugz` pages are unchanged.
 */
export function ToolboxFogbugzEvtxPage() {
  usePageTitle(FOGBUGZ_TOOL_NAME);
  const bridge = useToolboxBridge();
  const fogbugz = useFogbugzBridge();
  const support = useToolsetSupport("fogbugz");
  const { data: me } = useMe();

  if (!bridge) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <OpenInToolboxCallout reason="The FogBugz event log summary runs in SpecTree Toolbox: your own Claude Code writes the summary on your computer." />
      </div>
    );
  }

  if (support === undefined) {
    return (
      <div className="flex w-full px-4 pt-7">
        <Waiting label="Checking SpecTree Toolbox" />
      </div>
    );
  }

  if (support !== "supported" || !fogbugz) {
    const label = SUPPORT_LABELS[support === "spectree-update-needed" ? "spectree-update-needed" : "app-update-needed"];
    return (
      <div className="flex w-full flex-col gap-3 px-4 pb-6 pt-7" data-testid="fogbugz-update-needed">
        <h1 className={TOOLBOX_PAGE_TITLE}>{FOGBUGZ_TOOL_NAME}</h1>
        <div>
          <Chip tone={label.tone}>{label.text}</Chip>
        </div>
        <Link to={TOOLBOX_HOME_PATH} className="text-[13px] text-link hover:underline">
          Back to SpecTree Toolbox
        </Link>
      </div>
    );
  }

  return <FogbugzEvtxFlow fogbugz={fogbugz} personName={me?.name ?? "you"} />;
}
